/**
 * Panel Tooltip — dependency-free tooltip primitive (D1 decision) and the
 * rendering half of the suite's GLOBAL tooltip system (see
 * `tooltipConfig.ts` for the central timing/behavior store and
 * `public/suite-ui.css` for the shared `.suite-ui-tooltip` appearance).
 *
 * Replaces the vendored radix/shadcn tooltip for the shell. API-compatible
 * surface with what the shell uses: <TooltipProvider> (a passthrough root),
 * <Tooltip>, <TooltipTrigger asChild> (attaches hover/focus handlers to its
 * single child), and <TooltipContent side sideOffset className> rendered
 * through a portal so it always floats above panel chrome.
 *
 * Beyond the radix-shaped API the primitive coordinates globally: only one
 * tooltip is open suite-wide, an adjacent trigger within the central
 * skip-delay window opens instantly, and the shared config's kill switch
 * silences every tooltip at once. Content renders with the canonical
 * `suite-ui-tooltip` class unless a call site layers extra classes on.
 */

import React, {
    cloneElement,
    createContext,
    isValidElement,
    useCallback,
    useContext,
    useEffect,
    useId,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTooltipSystemConfig, type TooltipSide } from './tooltipConfig';
import { placeTooltipInViewport } from './tooltipPlacement';
// Loading the primitives arms the enforcement half: the browser-default
// title tooltip is retired for the whole document (self-installing bridge).
import './nativeTooltipBridge';

/**
 * Module-level coordination: the closer of the currently open tooltip and
 * when the last one closed — the skip-delay window reads the latter so a
 * pointer traveling across adjacent triggers never re-waits the full delay.
 */
let activeTooltipCloser: (() => void) | null = null;
let lastTooltipClosedAt = 0;

interface TooltipContextValue {
    open: boolean;
    openTooltip: () => void;
    /** Close after the central grace period; lets the pointer travel the
        side gap onto the content without the tooltip snapping shut. */
    scheduleCloseTooltip: () => void;
    closeTooltip: () => void;
    /** Registered by the trigger; the content positions against it */
    triggerRef: React.MutableRefObject<HTMLElement | null>;
    /** id of the rendered content — the trigger points aria-describedby at */
    contentId: string;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

const useTooltipContext = () => {
    const ctx = useContext(TooltipContext);
    if (!ctx) {
        throw new Error('Panel tooltip parts must be used within <Tooltip>');
    }
    return ctx;
};

export const TooltipProvider: React.FC<{ children?: React.ReactNode }> = ({
    children,
}) => <>{children}</>;

export interface TooltipProps {
    children?: React.ReactNode;
    /** Explicit per-tooltip open delay; falls back to the central config */
    delayDuration?: number;
    /** Gate evaluated (with the mounted trigger element) when hover or
        focus tries to open the tooltip; returning false keeps it closed.
        Re-evaluated when the delay timer fires, so a trigger resized
        during the hover delay re-validates. */
    shouldOpen?: (trigger: HTMLElement | null) => boolean;
}

export const Tooltip: React.FC<TooltipProps> = ({
    children,
    delayDuration,
    shouldOpen,
}) => {
    const config = useTooltipSystemConfig();
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLElement | null>(null);
    const openTimerRef = useRef<number | null>(null);
    const closeTimerRef = useRef<number | null>(null);
    // Mirrors of state/ref values read inside stable timers and the
    // module-level coordination slot.
    const openRef = useRef(false);
    const activeRef = useRef(false);
    const closeRef = useRef<() => void>(() => {});
    const contentId = useId();

    const delay = delayDuration ?? config.delayDuration;

    // Free the global slot: the skip-delay window measures from this moment
    const releaseActiveSlot = useCallback(() => {
        if (activeRef.current) {
            activeRef.current = false;
            if (activeTooltipCloser) activeTooltipCloser = null;
            lastTooltipClosedAt = Date.now();
        }
    }, []);

    const closeTooltip = useCallback(() => {
        if (openTimerRef.current !== null) {
            window.clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
        }
        if (closeTimerRef.current !== null) {
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
        openRef.current = false;
        setOpen(false);
        releaseActiveSlot();
    }, [releaseActiveSlot]);
    closeRef.current = closeTooltip;

    const openTooltip = useCallback(() => {
        if (config.disabled) return;
        if (closeTimerRef.current !== null) {
            // Re-entered during the close grace: the tooltip is still open
            window.clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
            return;
        }
        if (openTimerRef.current !== null || openRef.current) return;
        if (shouldOpen && !shouldOpen(triggerRef.current)) return;
        // Take the global slot from any other open tooltip immediately
        if (activeTooltipCloser && !activeRef.current) {
            activeTooltipCloser();
        }
        // An adjacent tooltip closed just now — skip the full hover delay
        const wait =
            Date.now() - lastTooltipClosedAt < config.skipDelayDuration
                ? 0
                : delay;
        const openNow = () => {
            openTimerRef.current = null;
            // Re-validate: the trigger may have resized during the delay
            if (shouldOpen && !shouldOpen(triggerRef.current)) return;
            openRef.current = true;
            setOpen(true);
            activeRef.current = true;
            activeTooltipCloser = () => closeRef.current();
        };
        if (wait === 0) openNow();
        else openTimerRef.current = window.setTimeout(openNow, wait);
    }, [config.disabled, config.skipDelayDuration, delay, shouldOpen]);

    const scheduleCloseTooltip = useCallback(() => {
        // Leaving while the open delay is still running: cancel silently
        if (openTimerRef.current !== null) {
            window.clearTimeout(openTimerRef.current);
            openTimerRef.current = null;
            return;
        }
        if (closeTimerRef.current !== null) return;
        const grace = config.closeDelayDuration;
        if (grace <= 0) {
            closeTooltip();
            return;
        }
        closeTimerRef.current = window.setTimeout(() => {
            closeTimerRef.current = null;
            closeTooltip();
        }, grace);
    }, [config.closeDelayDuration, closeTooltip]);

    // Drop any pending timers and the global slot when the tooltip
    // unmounts mid-hover
    useEffect(() => {
        return () => {
            if (openTimerRef.current !== null) {
                window.clearTimeout(openTimerRef.current);
                openTimerRef.current = null;
            }
            if (closeTimerRef.current !== null) {
                window.clearTimeout(closeTimerRef.current);
                closeTimerRef.current = null;
            }
            if (activeRef.current) {
                activeRef.current = false;
                activeTooltipCloser = null;
            }
        };
    }, []);

    return (
        <TooltipContext.Provider
            value={{
                open,
                openTooltip,
                scheduleCloseTooltip,
                closeTooltip,
                triggerRef,
                contentId,
            }}
        >
            {children}
        </TooltipContext.Provider>
    );
};

export interface TooltipTriggerProps {
    /** Render the handler-carrying element directly instead of a wrapper */
    asChild?: boolean;
    children?: React.ReactNode;
    className?: string;
}

export const TooltipTrigger: React.FC<TooltipTriggerProps> = ({
    asChild = false,
    children,
    className,
}) => {
    const ctx = useTooltipContext();

    if (asChild && isValidElement(children)) {
        const child = children as React.ReactElement<Record<string, unknown>>;
        // The child's own ref (if any) still fires; the trigger additionally
        // registers itself as the content's position anchor and points
        // aria-describedby at the open content for assistive tech.
        const describedBy =
            [
                child.props['aria-describedby'] as string | undefined,
                ctx.open ? ctx.contentId : undefined,
            ]
                .filter(Boolean)
                .join(' ') || undefined;
        return cloneElement(child, {
            onMouseEnter: (e: React.MouseEvent) => {
                (child.props.onMouseEnter as ((e: React.MouseEvent) => void) | undefined)?.(e);
                ctx.openTooltip();
            },
            onMouseLeave: (e: React.MouseEvent) => {
                (child.props.onMouseLeave as ((e: React.MouseEvent) => void) | undefined)?.(e);
                ctx.scheduleCloseTooltip();
            },
            onFocus: (e: React.FocusEvent) => {
                (child.props.onFocus as ((e: React.FocusEvent) => void) | undefined)?.(e);
                ctx.openTooltip();
            },
            onBlur: (e: React.FocusEvent) => {
                (child.props.onBlur as ((e: React.FocusEvent) => void) | undefined)?.(e);
                ctx.closeTooltip();
            },
            'aria-describedby': describedBy,
            ref: (node: HTMLElement | null) => {
                ctx.triggerRef.current = node;
                const ownRef = (child as unknown as { ref?: React.Ref<HTMLElement> }).ref;
                if (typeof ownRef === 'function') ownRef(node);
                else if (ownRef && typeof ownRef === 'object') {
                    // RefObject refs (useRef/createRef) can't be invoked —
                    // assign through so the child's own ref still lands.
                    (ownRef as { current: HTMLElement | null }).current = node;
                }
            },
        } as Partial<Record<string, unknown>>);
    }

    return (
        <span
            className={className}
            aria-describedby={ctx.open ? ctx.contentId : undefined}
            onMouseEnter={ctx.openTooltip}
            onMouseLeave={ctx.scheduleCloseTooltip}
            onFocus={ctx.openTooltip}
            onBlur={ctx.closeTooltip}
            ref={(node: HTMLSpanElement | null) => {
                ctx.triggerRef.current = node;
            }}
        >
            {children}
        </span>
    );
};

export interface TooltipContentProps {
    /** Overrides the central config's default side */
    side?: TooltipSide;
    /** Overrides the central config's default trigger→content gap */
    sideOffset?: number;
    /** Extra classes layered ON TOP of the canonical `suite-ui-tooltip`
        surface (e.g. an app's local theme mapping) */
    className?: string;
    children?: React.ReactNode;
}

export const TooltipContent: React.FC<TooltipContentProps> = ({
    side,
    sideOffset,
    className,
    children,
}) => {
    const ctx = useTooltipContext();
    const config = useTooltipSystemConfig();
    const resolvedSide = side ?? config.side;
    const resolvedOffset = sideOffset ?? config.sideOffset;
    const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    const computePosition = useCallback(() => {
        const trigger = ctx.triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const size = contentRef.current?.getBoundingClientRect();
        // The shared global rule: flip to the opposite side when the window
        // has no room for the preferred one (a top-bar trigger has nothing
        // above it), then clamp — never outside the window, never covering
        // the pointer resting on the anchor.
        const placement = placeTooltipInViewport({
            anchor: rect,
            width: size?.width ?? 0,
            height: size?.height ?? 0,
            side: resolvedSide,
            sideOffset: resolvedOffset,
        });
        setPosition({ left: placement.left, top: placement.top });
    }, [ctx.triggerRef, resolvedSide, resolvedOffset]);

    useLayoutEffect(() => {
        if (!ctx.open) {
            setPosition(null);
            return;
        }
        computePosition();
    }, [ctx.open, computePosition]);

    useEffect(() => {
        if (!ctx.open) return;
        const handleViewportChange = () => computePosition();
        window.addEventListener('resize', handleViewportChange);
        window.addEventListener('scroll', handleViewportChange, true);
        return () => {
            window.removeEventListener('resize', handleViewportChange);
            window.removeEventListener('scroll', handleViewportChange, true);
        };
    }, [ctx.open, computePosition]);

    // Mount the content even before its position is known: the layout
    // effect below measures the real rendered size through the ref, so the
    // first placement is never computed off a 0×0 assumption (which used to
    // shove edge-anchored tooltips out of the window or onto the trigger).
    // Hidden until placed — the measure-and-pin pass runs before the
    // browser paints, so the unplaced frame is never visible.
    if (!ctx.open || config.disabled) return null;

    return createPortal(
        <div
            ref={contentRef}
            role="tooltip"
            id={ctx.contentId}
            className={
                className
                    ? `suite-ui-tooltip ${className}`
                    : 'suite-ui-tooltip'
            }
            style={{
                position: 'fixed',
                left: position?.left ?? 0,
                top: position?.top ?? 0,
                visibility: position ? 'visible' : 'hidden',
                zIndex: 120,
                pointerEvents: 'none',
            }}
            onMouseEnter={ctx.openTooltip}
            onMouseLeave={ctx.scheduleCloseTooltip}
        >
            {children}
        </div>,
        document.body,
    );
};
